import React, { createContext, useState, useCallback, ReactNode } from 'react';
import endpoints from '../utils/commonVariables/endpoints';
import api from '../services/api';
import {
    formatMostBookedHotelsData,
    formatUpcomingBookingsData,
    getPeriodByKey,
    UPCOMING_BOOKINGS_STATUS_KEY,
} from '../utils/dashboard/dashbordUtils';
import { useNotification } from './NotificationsContext';
import { FeedbackMessages } from '../utils/commonVariables/messages';
import { buildEndPoint } from '../utils/odataEndPoint';
import { readOnlyEntities } from '../utils/commonVariables/entityKeys';
import {
    convertDateToUTC,
    formatDateToEndOfDayUTC,
} from '../utils/formatDates';

export enum PastPeriodKey {
    CURRENT = 'current',
    LAST_MONTH = 'last_month',
    LAST_3_MONTHS = 'last_3_months',
    LAST_6_MONTHS = 'last_6_months',
    LAST_12_MONTHS = 'last_12_months',
}

export enum UpcomingPeriodKey {
    NEXT_7_DAYS = 'next_7_days',
    NEXT_14_DAYS = 'next_14_days',
    NEXT_30_DAYS = 'next_30_days',
}

export interface HistoryBookingsData {
    DateStats: string;
    CommitedCount: number;
    CommitedNights: number;
    CommitedTotal: number; //decimal
    CancelledCount: number;
    CancelledNights: number;
    CancelledTotal: number; //decimal
}

export interface HotelsBookedData {
    BookingCount: number;
    IdHotel: number;
    HotelName: string;
}

export interface UpcomingBooking {
    BookingCode: string;
    Id: string;
    IdSupplierHotel: number;
    SupplierHotel: string;
    CheckInDate: Date;
    CheckOutDate: Date;
    Nights: number;
}

interface ChartState {
    isLoading: boolean;
    isDataLoaded: boolean;
    period: {
        start: string;
        end: string;
        key: PastPeriodKey;
    };
}

export interface HistoryBookingsState extends ChartState {
    data: HistoryBookingsData[];
}

interface MostBookedHotelsState extends ChartState {
    data: HotelsBookedData[];
}

interface UpcomingBookingsState {
    isLoading: boolean;
    isDataLoaded: boolean;
    period: {
        start: string;
        end: string;
        key: UpcomingPeriodKey;
    };

    data: UpcomingBooking[];
}

interface DashboardContextType {
    bookingsHistory: HistoryBookingsState;
    hotelsBooked: MostBookedHotelsState;
    upcomingBookings: UpcomingBookingsState;
    getChartsInitialData: () => void;
    changeBookingsHistoryPeriod: (newPeriod: PastPeriodKey) => void;
    changeHotelsBookedPeriod: (newPeriod: PastPeriodKey) => void;
    changeUpcomingBookingsPeriod: (newPeriod: UpcomingPeriodKey) => void;
}

export const ContextDashboard = createContext<DashboardContextType | undefined>(
    undefined
);

const DEFAULT_PAST_PERIOD_KEY = PastPeriodKey.LAST_MONTH;
const DEFAULT_UPCOMING_PERIOD_KEY = UpcomingPeriodKey.NEXT_7_DAYS;
const initialPastPeriod = getPeriodByKey(DEFAULT_PAST_PERIOD_KEY);
const initialUpcomingPeriod = getPeriodByKey(DEFAULT_UPCOMING_PERIOD_KEY);

export const ProviderDashboard = ({ children }: { children: ReactNode }) => {
    const { setErrorMessage } = useNotification();

    const [bookingsHistory, setBookingsHistory] =
        useState<HistoryBookingsState>({
            isLoading: false,
            isDataLoaded: false,
            data: [],
            period: {
                start: initialPastPeriod.start,
                end: initialPastPeriod.end,
                key: DEFAULT_PAST_PERIOD_KEY,
            },
        });

    const [hotelsBooked, setHotelsBooked] = useState<MostBookedHotelsState>({
        isLoading: false,
        isDataLoaded: false,
        data: [],
        period: {
            start: initialPastPeriod.start,
            end: initialPastPeriod.end,
            key: DEFAULT_PAST_PERIOD_KEY,
        },
    });

    const [upcomingBookings, setUpcomingBookings] =
        useState<UpcomingBookingsState>({
            isLoading: false,
            isDataLoaded: false,
            data: [],
            period: {
                start: initialUpcomingPeriod.start,
                end: initialUpcomingPeriod.end,
                key: DEFAULT_UPCOMING_PERIOD_KEY,
            },
        });

    const getHistoryBookingsData = async (
        start: string = bookingsHistory.period.start,
        end: string = bookingsHistory.period.end
    ) => {
        const endpoint = endpoints.bookingStats(start, end);
        try {
            const { data } = await api.get(endpoint);

            setBookingsHistory(prev => ({
                ...prev,
                isLoading: false,
                isDataLoaded: true,
                data: data,
            }));
        } catch (error) {
            console.log(error);
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
            setBookingsHistory(prev => ({
                ...prev,
                isLoading: false,
                isDataLoaded: true,
                data: [],
            }));
        }
    };

    const getHotelsBooked = async (
        start: string = hotelsBooked.period.start,
        end: string = hotelsBooked.period.end
    ) => {
        const endpoint = endpoints.mostBookedHotels(start, end);
        try {
            const { data } = await api.get(endpoint);

            const formatedData = formatMostBookedHotelsData(data);
            setHotelsBooked(prev => ({
                ...prev,
                isLoading: false,
                isDataLoaded: true,
                data: formatedData,
            }));
        } catch (error) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
            setHotelsBooked(prev => ({
                ...prev,
                isLoading: false,
                isDataLoaded: true,
                data: [],
            }));
        }
    };

    const getUpcomingBookings = async (
        start: string = upcomingBookings.period.start,
        end: string = upcomingBookings.period.end
    ) => {
        const endpoint = buildEndPoint({
            entityName: readOnlyEntities.SupplierBooking,
            query: {
                expand: [readOnlyEntities.SupplierHotel],
                select: [
                    'Id',
                    'BookingCode',
                    'IdSupplierHotel',
                    'SupplierHotel/Name',
                    'CheckInDate',
                    'CheckOutDate',
                    'Nights',
                ],

                orderBy: ['CheckInDate asc'],
                filter: [
                    `(CheckInDate ge datetime'${convertDateToUTC(
                        start
                    )}') and (CheckInDate le datetime'${formatDateToEndOfDayUTC(
                        end
                    )}') and (SupplierBookingStatus/SupplierBookingStatusType/Key eq '${UPCOMING_BOOKINGS_STATUS_KEY}')`,
                ],
            },
        });
        try {
            const { data } = await api.get(endpoint);
            const formatedData = formatUpcomingBookingsData(data.value);
            setUpcomingBookings(prev => ({
                ...prev,
                isLoading: false,
                isDataLoaded: true,
                data: formatedData,
            }));
        } catch (error) {
            console.log(error);
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
            setUpcomingBookings(prev => ({
                ...prev,
                isLoading: false,
                isDataLoaded: true,
                data: [],
            }));
        }
    };

    const getChartsInitialData = useCallback(async () => {
        setBookingsHistory(prev => ({
            ...prev,
            isLoading: true,
            isDataLoaded: false,
            data: [],
        }));
        setHotelsBooked(prev => ({
            ...prev,
            isLoading: true,
            isDataLoaded: false,
            data: [],
        }));
        setUpcomingBookings(prev => ({
            ...prev,
            isLoading: true,
            isDataLoaded: false,
            data: [],
        }));

        getHotelsBooked();
        getHistoryBookingsData();
        getUpcomingBookings();
    }, []);

    const changeBookingsHistoryPeriod = async (newPeriod: PastPeriodKey) => {
        const newPeriodData = getPeriodByKey(newPeriod);
        setBookingsHistory(prev => ({
            ...prev,
            isLoading: true,
            isDataLoaded: false,
            data: [],
            period: {
                ...prev.period,
                start: newPeriodData.start,
                end: newPeriodData.end,
                key: newPeriod,
            },
        }));
        await getHistoryBookingsData(newPeriodData.start, newPeriodData.end);
    };

    const changeHotelsBookedPeriod = async (newPeriod: PastPeriodKey) => {
        const newPeriodData = getPeriodByKey(newPeriod);
        setHotelsBooked(prev => ({
            ...prev,
            isLoading: true,
            isDataLoaded: false,
            data: [],
            period: {
                ...prev.period,
                start: newPeriodData.start,
                end: newPeriodData.end,
                key: newPeriod,
            },
        }));
        await getHotelsBooked(newPeriodData.start, newPeriodData.end);
    };

    const changeUpcomingBookingsPeriod = async (
        newPeriod: UpcomingPeriodKey
    ) => {
        const newPeriodData = getPeriodByKey(newPeriod);
        setUpcomingBookings(prev => ({
            ...prev,
            isLoading: true,
            isDataLoaded: false,
            data: [],
            period: {
                ...prev.period,
                start: newPeriodData.start,
                end: newPeriodData.end,
                key: newPeriod,
            },
        }));
        await getUpcomingBookings(newPeriodData.start, newPeriodData.end);
    };

    return (
        <ContextDashboard.Provider
            value={{
                bookingsHistory,
                hotelsBooked,
                upcomingBookings,
                getChartsInitialData,
                changeBookingsHistoryPeriod,
                changeHotelsBookedPeriod,
                changeUpcomingBookingsPeriod,
            }}
        >
            {children}
        </ContextDashboard.Provider>
    );
};

export const useDashboard = () => {
    const context = React.useContext(ContextDashboard);
    if (context === undefined) {
        throw new Error('useDashboard must be used within a ProviderDashboard');
    }
    return context;
};

export const useHotelsBooked = () => {
    const context = React.useContext(ContextDashboard);
    if (context === undefined) {
        throw new Error(
            'useHotelsBooked must be used within a ProviderDashboard'
        );
    }
    return context.hotelsBooked;
};

export const useHistoryBookings = () => {
    const context = React.useContext(ContextDashboard);
    if (context === undefined) {
        throw new Error(
            'useHistoryBookings must be used within a ProviderDashboard'
        );
    }
    return context.bookingsHistory;
};

export const useHistoryBookingsKPIs = () => {
    const context = React.useContext(ContextDashboard);
    if (context === undefined) {
        throw new Error(
            'useHistoryBookingsKPIs must be used within a ProviderDashboard'
        );
    }
    const { bookingsHistory } = context;

    const totalConfirmed = bookingsHistory.data.reduce(
        (acc, item) => acc + item.CommitedCount,
        0
    );
    const totalCancelled = bookingsHistory.data.reduce(
        (acc, item) => acc + item.CancelledCount,
        0
    );
    const total = totalConfirmed + totalCancelled;
    const cancelledPercentage = total > 0 ? (totalCancelled / total) * 100 : 0;
    const totalNights = bookingsHistory.data.reduce(
        (acc, item) => acc + item.CommitedNights,
        0
    );

    const los = totalConfirmed > 0 ? totalNights / totalConfirmed : 0;

    return {
        isLoading: bookingsHistory.isLoading,
        isDataLoaded: bookingsHistory.isDataLoaded,
        totalConfirmed,
        totalCancelled,
        cancelledPercentage,
        totalNights,
        los,
        period: bookingsHistory.period,
        isDataAvailable: bookingsHistory.data.length > 0,
    };
};
